import React, { useState } from "react";
import { Link } from "react-router-dom";
import emailjs from "emailjs-com";

function Careers() {
  const serviceID = "service_eo5xzzf";
  const templateID = "template_nfbbjo5";
  const userID = "r_R1sDNYxS2eBrolP";

  // Cloudinary credentials
  const CLOUD_NAME = "fpljmxds";
  const UPLOAD_PRESET = "careers_resumes"; // Replace with your actual upload preset name

  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [formData, setFormData] = useState({
    from_name: "",
    reply_to: "",
    contactNumber: "",
    subject: "",
    message: "",
  });

  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setResumeFile(null);
      return;
    }

    // Allowed file types: PDF, DOC, DOCX
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const allowedExtensions = [".pdf", ".doc", ".docx"];
    const fileExtension = "." + file.name.split(".").pop().toLowerCase();

    // Type validation
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      alert("Invalid format. Please upload a PDF, DOC, or DOCX document.");
      e.target.value = "";
      setResumeFile(null);
      return;
    }

    // Size validation: Cloudinary maximum limit is 10 MB
    const maxSizeBytes = 10 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      alert("File size exceeds 10 MB. Please upload a file smaller than 10 MB.");
      e.target.value = "";
      setResumeFile(null);
      return;
    }

    setResumeFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!resumeFile) {
      alert("Please upload your resume before submitting the application.");
      return;
    }

    setLoading(true);

    try {
      // Step 1: Upload resume to Cloudinary
      const uploadData = new FormData();
      uploadData.append("file", resumeFile);
      uploadData.append("upload_preset", UPLOAD_PRESET);

      const cloudinaryRes = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/auto/upload`,
        {
          method: "POST",
          body: uploadData,
        }
      );

      const cloudinaryJson = await cloudinaryRes.json();
      if (!cloudinaryRes.ok || !cloudinaryJson.secure_url) {
        throw new Error(cloudinaryJson.error?.message || "Failed to upload file to Cloudinary");
      }

      const fileUrl = cloudinaryJson.secure_url;

      // Step 2: Send structured parameters with resume_url to EmailJS
      const templateParams = {
        from_name: formData.from_name,
        reply_to: formData.reply_to,
        contactNumber: formData.contactNumber,
        subject: formData.subject,
        message: formData.message,
        resume_url: fileUrl,
      };

      await emailjs.send(serviceID, templateID, templateParams, userID);

      alert("Your application has been sent successfully. Our recruitment team will contact you shortly.");
      setShowForm(false);
      setResumeFile(null);
      setFormData({
        from_name: "",
        reply_to: "",
        contactNumber: "",
        subject: "",
        message: "",
      });
      e.target.reset();
    } catch (error) {
      console.error("Career form submission error:", error);
      const subject = encodeURIComponent(`Career Application: ${formData.subject || "Open Position"}`);
      const body = encodeURIComponent(
        `Full Name: ${formData.from_name}\nEmail: ${formData.reply_to}\nPhone: ${formData.contactNumber}\nPosition: ${formData.subject}\n\nMessage:\n${formData.message || ""}`
      );
      window.location.href = `mailto:careers@polopvc.com?subject=${subject}&body=${body}`;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full bg-slate-50/50 font-sans overflow-hidden">
      {/* Premium Hero Section */}
      <div className="relative w-full bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 pt-20 pb-28 md:pt-24 md:pb-36 lg:pt-28 lg:pb-40 px-6 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-4xl mx-auto text-center text-white space-y-4 relative z-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-2 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
            <span className="text-xs font-bold uppercase tracking-wider text-orange-50">Join Our Team</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight drop-shadow-md">
            Careers at PoloPipes
          </h1>
          <p className="text-sm sm:text-base md:text-lg font-normal leading-relaxed text-orange-50 opacity-95 max-w-2xl mx-auto">
            Embark on a rewarding career journey with an industry-leading polymer piping manufacturer. We foster growth, technical excellence, and sustainable innovation.
          </p>
        </div>

        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-[0] z-10">
          <svg
            className="relative block w-full h-[60px] md:h-[100px]"
            viewBox="0 0 1920 120"
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0,120 C640,30 1280,30 1920,120 L1920,120 L0,120 Z"
              fill="#ffffff"
            />
          </svg>
        </div>
      </div>

      {/* Breadcrumb Navigation Bar */}
      <div className="bg-white py-3.5 px-6 sm:px-12 border-b border-slate-100 relative z-20">
        <div className="max-w-7xl mx-auto flex items-center space-x-2 text-xs sm:text-sm font-medium text-slate-500">
          <Link to="/" className="text-orange-600 hover:text-orange-700 font-semibold transition-colors">Home</Link>
          <span className="text-slate-300 font-light">&gt;</span>
          <span className="text-slate-800">Careers</span>
        </div>
      </div>

      {/* Vacancies Section */}
      <section className="py-16 md:py-24 relative">
        <div className="absolute top-1/3 -left-40 w-96 h-96 bg-orange-200/20 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-5xl mx-auto px-6 relative z-10 space-y-10">
          <div className="text-center space-y-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-orange-100 text-orange-700">
              Work With Us
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Current Openings
            </h2>
            <p className="text-slate-600 text-xs sm:text-sm max-w-md mx-auto">
              Explore active job opportunities and apply to join our manufacturing facility operations.
            </p>
          </div>

          <div className="w-full rounded-3xl border border-slate-200/80 shadow-xl shadow-slate-200/50 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-100 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    <th className="px-6 py-4">Job Title</th>
                    <th className="px-6 py-4">Timing</th>
                    <th className="px-6 py-4">Department</th>
                    <th className="px-6 py-4">Experience</th>
                    <th className="px-6 py-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs sm:text-sm text-slate-600">
                  <tr className="hover:bg-orange-50/30 transition-colors">
                    <td className="px-6 py-5">
                      <div className="font-bold text-slate-900 text-sm sm:text-base">Machine Operator</div>
                      <span className="text-[10px] text-slate-400 font-medium sm:hidden">Full-Time</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-sky-50 text-sky-700 border border-sky-100">
                        9:00 AM – 6:00 PM
                      </span>
                    </td>
                    <td className="px-6 py-5 font-medium text-slate-600">
                      Machine & Production
                    </td>
                    <td className="px-6 py-5 font-semibold text-slate-800">
                      3–10 years
                    </td>
                    <td className="px-6 py-5 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setShowForm(true);
                          setFormData((prev) => ({ ...prev, subject: "Machine Operator" }));
                        }}
                        className="inline-flex items-center justify-center bg-slate-900 hover:bg-orange-600 text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-md transition-all duration-300 hover:shadow-lg cursor-pointer"
                      >
                        Apply Now
                      </button>
                    </td>
                  </tr>
                  <tr className="hover:bg-orange-50/30 transition-colors">
                    <td className="px-6 py-5">
                      <div className="font-bold text-slate-900 text-sm sm:text-base">Sales Executive</div>
                      <span className="text-[10px] text-slate-400 font-medium sm:hidden">Full-Time</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                        9:00 AM – 6:00 PM
                      </span>
                    </td>
                    <td className="px-6 py-5 font-medium text-slate-600">
                      Sales & Marketing
                    </td>
                    <td className="px-6 py-5 font-semibold text-slate-800">
                      5–10 years
                    </td>
                    <td className="px-6 py-5 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setShowForm(true);
                          setFormData((prev) => ({ ...prev, subject: "Sales Executive" }));
                        }}
                        className="inline-flex items-center justify-center bg-slate-900 hover:bg-orange-600 text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-md transition-all duration-300 hover:shadow-lg cursor-pointer"
                      >
                        Apply Now
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {showForm && (
            <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl shadow-slate-200/50 p-6 md:p-8 max-w-2xl mx-auto">
              <div className="flex items-center justify-between gap-4 mb-6">
                <div>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-orange-100 text-orange-700">
                    Application Form
                  </span>
                  <h3 className="mt-3 text-2xl font-black text-slate-900 tracking-tight">Apply for Career Opportunity</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="text-slate-500 hover:text-slate-800 text-xl font-bold"
                  aria-label="Close apply form"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col">
                    <label className="font-semibold text-xs text-slate-700 mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      name="from_name"
                      required
                      value={formData.from_name}
                      onChange={onChangeHandler}
                      placeholder="Your full name"
                      className="w-full border border-slate-200 rounded-xl h-11 px-4 text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all bg-slate-50/30"
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="font-semibold text-xs text-slate-700 mb-1.5">Email Address *</label>
                    <input
                      type="email"
                      name="reply_to"
                      required
                      value={formData.reply_to}
                      onChange={onChangeHandler}
                      placeholder="your@email.com"
                      className="w-full border border-slate-200 rounded-xl h-11 px-4 text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all bg-slate-50/30"
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="font-semibold text-xs text-slate-700 mb-1.5">Phone Number *</label>
                    <input
                      type="tel"
                      name="contactNumber"
                      required
                      value={formData.contactNumber}
                      onChange={onChangeHandler}
                      placeholder="+91 98765 43210"
                      className="w-full border border-slate-200 rounded-xl h-11 px-4 text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all bg-slate-50/30"
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="font-semibold text-xs text-slate-700 mb-1.5">Position Applied *</label>
                    <input
                      type="text"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={onChangeHandler}
                      placeholder="Machine Operator / Sales Executive"
                      className="w-full border border-slate-200 rounded-xl h-11 px-4 text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all bg-slate-50/30"
                    />
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="font-semibold text-xs text-slate-700 mb-1.5">Cover Letter / Additional Details</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={onChangeHandler}
                    rows="5"
                    placeholder="Tell us about your background, relevant experience, and why you want to join PoloPipes."
                    className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all bg-slate-50/30 resize-none"
                  />
                </div>

                {/* Upload Resume with Instruction and Format Badges */}
                <div className="flex flex-col">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="font-semibold text-xs text-slate-700">Upload Resume *</label>
                    <span className="text-[11px] font-medium text-slate-400">Max size: 10 MB</span>
                  </div>

                  <input
                    type="file"
                    name="resume"
                    accept=".pdf,.doc,.docx"
                    required
                    onChange={handleFileChange}
                    className="w-full border border-dashed border-slate-300 rounded-2xl h-12 px-4 py-2 text-xs sm:text-sm text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-orange-100 file:text-orange-700 file:font-semibold file:cursor-pointer hover:border-orange-400 transition-colors"
                  />

                  <div className="flex items-center gap-1.5 mt-2 text-[11px] text-slate-500">
                    <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span>Accepted formats: <strong className="font-medium text-slate-600">.pdf, .doc, .docx</strong> (maximum 10 MB)</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 text-white text-sm font-bold px-5 py-3 rounded-xl shadow-lg shadow-orange-500/20 hover:opacity-95 transition-all disabled:opacity-60 cursor-pointer"
                  >
                    {loading ? "Uploading & Sending..." : "Submit Application"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="inline-flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-bold px-5 py-3 rounded-xl transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* General Resume Box */}
          <div className="bg-gradient-to-br from-orange-50/80 to-amber-50/50 rounded-3xl p-8 border border-orange-100 text-center max-w-2xl mx-auto space-y-3 shadow-sm">
            <h3 className="text-base font-bold text-slate-900">
              Don't see a position that fits?
            </h3>
            <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
              We are always interested in connecting with talented engineering, operations, and logistics professionals. Send your resume directly to our recruitment team:
            </p>
            <div className="pt-1">
              <a
                href="mailto:careers@polopvc.com"
                className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-orange-600 bg-white px-4 py-2 rounded-xl border border-orange-200 hover:bg-orange-600 hover:text-white transition-all shadow-xs"
              >
                careers@polopvc.com
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Careers;